/**
 * EnhancedResultsDashboard component
 * A more interactive and visually appealing dashboard for assessment results
 */
import React, { useEffect, useState } from 'react';
import './enhancedDashboard.css';
import cannedResponses from '../../../assessment/data/cannedResponses.json';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';
import { Category } from '../../../assessment/models/Question';
import { Button } from '@/components/ui/button';
import { Download, TrendingUp, Award, ChevronRight, Calendar } from 'lucide-react';

// Define types
export type MaturityLevel = 'build' | 'elevate' | 'optimize';

export interface CategoryScore {
  categoryName: string; // Changed from 'category'
  score: number;
  maxPossible: number;
  percentage: number;
}

interface EnhancedResultsDashboardProps {
  categoryResults: CategoryScore[]; // Changed from categoryScores
  overallScorePercentage: number; // New prop for overall score as percentage
  maturityLevel: MaturityLevel;
  onDownloadPdf: () => void;
}

// Helper function to get display name for categories
const getCategoryDisplayName = (category: Category): string => {
  const displayNames: Record<Category, string> = {
    'strategy-alignment': 'Strategy & Alignment',
    'people': 'People & Leadership',
    'portfolio-reporting': 'Portfolio & Reporting',
    'scalability-growth': 'Scalability & Growth',
    'tech-tools': 'Technology & Tools',
    'value-focus': 'Value Focus',
    'change-transformation': 'Change & Transformation',
    'process-governance': 'Process & Governance'
  };
  return displayNames[category] || category;
};

// Helper function to calculate percentage based on score and max possible
const calculatePercentage = (score: number, maxPossible: number): number => {
  return Math.round((score / maxPossible) * 100);
};

// Helper function to normalize percentage display (handles both 0-1 and 0-100 ranges)
const normalizePercentage = (percentage: number): number => {
  console.log(`[normalizePercentage] Input percentage: ${percentage}, Type: ${typeof percentage}`);
  
  // If percentage is already in 0-100 range, return as is
  // Otherwise convert from 0-1 range to 0-100 range
  const result = percentage > 1 ? percentage : Math.round(percentage * 100);
  
  console.log(`[normalizePercentage] Normalized result: ${result}`);
  return result;
};

// Format data for radar chart
const formatRadarData = (categoryResults: CategoryScore[]) => { // Changed from categoryScores
  console.log('[formatRadarData] Processing category results for radar chart:', categoryResults);
  
  const radarData = categoryResults.map(item => {
    const normalizedValue = normalizePercentage(item.percentage);
    console.log(`[formatRadarData] Category: ${item.categoryName}, Raw percentage: ${item.percentage}, Normalized: ${normalizedValue}`);
    
    return {
      subject: getCategoryDisplayName(item.categoryName as Category), // Use full display name
      A: normalizedValue, // Use normalized percentage
      fullMark: 100
    };
  });
  
  console.log('[formatRadarData] Final radar data:', radarData);
  return radarData;
};


// Maturity level display data based on tool-concept.md scoring model
const maturityLevels = {
  build: {
    title: 'Build Stage',
    description: 'Your PMO is at an initial level. Processes and governance exist only in basic form, and alignment to strategy is still emerging.',
    color: '#1E40AF', // deepBlue from home page
    nextStage: 'Elevate',
    percentile: '35th',
    scoreRange: '20-39'
  },
  elevate: {
    title: 'Elevate Stage',
    description: 'Your PMO is developing. Repeatable processes and standard tools are in place, and the PMO increasingly supports strategic goals.',
    color: '#1E40AF', // deepBlue from home page
    nextStage: 'Optimize',
    percentile: '65th',
    scoreRange: '40-59'
  },
  optimize: {
    title: 'Optimize Stage',
    description: 'Your PMO operates at an advanced level. It embeds change management and strategic agility into its culture.',
    color: '#1E40AF', // deepBlue from home page
    nextStage: 'Maintain Excellence',
    percentile: '90th',
    scoreRange: '60-80'
  }
};

// Quick wins based on maturity level
const quickWins = {
  build: {
    title: 'Establish a PMO Charter',
    description: "Define your PMO's mission, scope, and governance structure to create clarity and alignment.",
    impact: 'High'
  },
  elevate: {
    title: 'Implement Portfolio Prioritization',
    description: 'Create a formal scoring model to evaluate and prioritize projects based on strategic value.',
    impact: 'High'
  },
  optimize: {
    title: 'Adopt Predictive Analytics',
    description: 'Leverage AI and data analytics to forecast project outcomes and optimize resource allocation.',
    impact: 'High'
  }
};

// Helper function to get targeted recommendations based on weakest categories
const getRecommendations = (maturityLevel: MaturityLevel, weakCategories: CategoryScore[]) => { // weakCategories items now have categoryName
  // Get the canned responses for the current maturity level
  const levelResponses = cannedResponses[maturityLevel];
  
  console.log(`[getRecommendations] Maturity level: ${maturityLevel}`);
  console.log(`[getRecommendations] Available sections for ${maturityLevel}:`, 
    levelResponses.sections.map((s: any) => s.title));
  
  // Find relevant sections for the three weakest categories
  const findRecommendationForCategory = (category: CategoryScore, index: number) => {
    if (!category) {
      console.log('[getRecommendations] Category is null or undefined');
      return null;
    }
    
    const categoryDisplayName = getCategoryDisplayName(category.categoryName as Category);
    console.log(`[getRecommendations] Finding recommendation for category: ${category.categoryName} (${categoryDisplayName})`);
    
    // Find sections that match the category
    const matchingSections = levelResponses.sections.filter((section: any) => 
      section.category === categoryDisplayName
    );
    
    if (matchingSections.length > 0) {
      // If we have matching sections, use the first one
      const section = matchingSections[0];
      console.log(`[getRecommendations] Found matching section: "${section.title}" with category "${section.category}"`);
      
      return {
        category: categoryDisplayName,
        score: category.percentage,
        title: section.title,
        recommendations: section.recommendations
      };
    } else {
      // Fallback to using index if no matching category is found
      console.log(`[getRecommendations] No matching section found for category: ${categoryDisplayName}, using fallback`);
      const sectionIndex = Math.min(index, levelResponses.sections.length - 1);
      const fallbackSection = levelResponses.sections[sectionIndex];
      
      return {
        category: categoryDisplayName,
        score: category.percentage,
        title: fallbackSection.title,
        recommendations: fallbackSection.recommendations
      };
    }
  };
  
  // Get recommendations for the three weakest categories
  const quickWin = findRecommendationForCategory(weakCategories[0], 0);
  const mediumTerm = findRecommendationForCategory(weakCategories[1], 1);
  const longTerm = findRecommendationForCategory(weakCategories[2], 2);
  
  return {
    quickWin,
    mediumTerm,
    longTerm,
    maturityLevel: {
      title: levelResponses.title,
      description: levelResponses.description
    }
  };
};

const EnhancedResultsDashboard: React.FC<EnhancedResultsDashboardProps> = ({
  categoryResults, // Changed from categoryScores
  overallScorePercentage, // Changed from overallScore and maxPossibleScore
  maturityLevel,
  onDownloadPdf
}) => {
  // Debug logging for props
  console.log('[EnhancedResultsDashboard] Props received:', {
    categoryResults,
    overallScorePercentage,
    maturityLevel
  });
  
  // Log each category score in detail
  if (categoryResults && categoryResults.length > 0) {
    console.log('[EnhancedResultsDashboard] Category scores detail:');
    categoryResults.forEach((category, index) => {
      console.log(`Category ${index + 1}: ${category.categoryName}`);
      console.log(`  - Score: ${category.score}`);
      console.log(`  - Max Possible: ${category.maxPossible}`);
      console.log(`  - Percentage: ${category.percentage} (${category.percentage > 1 ? 'appears to be 0-100 scale' : 'appears to be 0-1 scale'})`); 
    });
  }
  // State for card flipping
  const [isRadarCardFlipped, setIsRadarCardFlipped] = useState(false);
  const [isStrengthsCardFlipped, setIsStrengthsCardFlipped] = useState(false);
  
  // Display raw score directly
  // The overallScorePercentage is actually the raw score, not a percentage
  const totalActualScore = overallScorePercentage;
  
  // Calculate actual percentage for visualizations (out of 80 max possible points)
  const scorePercentage = (totalActualScore / 80) * 100;
  const currentLevel = maturityLevels[maturityLevel];
  const quickWin = quickWins[maturityLevel];
  
  // Sort scores to find strengths and weaknesses
  const sortedScores = [...categoryResults].sort((a, b) => b.percentage - a.percentage); // Changed from categoryScores
  const strengths = sortedScores.slice(0, 3);
  const weaknesses = sortedScores.slice(-3).reverse();
  
  // Debug logging for category scores and weaknesses
  console.log('[EnhancedResultsDashboard] All category scores:', JSON.stringify(categoryResults, null, 2));
  console.log('[EnhancedResultsDashboard] Sorted scores:', JSON.stringify(sortedScores, null, 2));
  console.log('[EnhancedResultsDashboard] Weaknesses (3 lowest scores, reversed):', JSON.stringify(weaknesses, null, 2));
  
  // Check if we have proper category names in the data
  if (categoryResults && categoryResults.length > 0) {
    console.log('[EnhancedResultsDashboard] First category structure:', {
      categoryName: categoryResults[0].categoryName,
      category: (categoryResults[0] as any).category, // Check if old property exists
      score: categoryResults[0].score,
      percentage: categoryResults[0].percentage
    });
  }
  
  // Get targeted recommendations based on weakest categories
  const recommendations = getRecommendations(maturityLevel, weaknesses);
  console.log('[EnhancedResultsDashboard] Generated recommendations:', recommendations);
  
  // Format radar data
  const radarData = formatRadarData(categoryResults); // Use categoryResults
  
  // Function to toggle card flip
  const flipRadarCard = () => setIsRadarCardFlipped(!isRadarCardFlipped);
  const flipStrengthsCard = () => setIsStrengthsCardFlipped(!isStrengthsCardFlipped);
  
  return (
    <div className="w-full bg-white rounded-lg shadow-md overflow-hidden">
      {/* Executive Summary Section */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Your PMO Maturity Assessment</h2>
            <p className="text-gray-600 mt-1">
              Completed on {new Date().toLocaleDateString()}
            </p>
          </div>
          <Button 
            onClick={onDownloadPdf}
            className="text-white"
            style={{ backgroundColor: '#EF4444', borderColor: '#EF4444' }}
          >
            <Download className="mr-2 h-4 w-4" /> Export Results
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Overall Score */}
          <div className="bg-gray-50 rounded-lg p-4 flex items-center">
            <div className="relative h-28 w-28 flex items-center justify-center">
              <svg className="h-full w-full" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="#e6e6e6"
                  strokeWidth="10"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke={currentLevel.color}
                  strokeWidth="10"
                  strokeDasharray={`${2 * Math.PI * 45 * scorePercentage / 100} ${2 * Math.PI * 45 * (1 - scorePercentage / 100)}`}
                  strokeDashoffset={2 * Math.PI * 45 * 0.25}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-bold">{totalActualScore} / 80</span>
                {/* Removed maxPossibleScore as overall is now a percentage */}
              </div>
            </div>
            <div className="ml-4">
              <h3 className="font-semibold text-lg" style={{ color: currentLevel.color }}>
                {currentLevel.title} <span className="text-sm font-normal">({currentLevel.scoreRange})</span>
              </h3>
              <p className="text-sm text-gray-600 mt-1">Overall Maturity Score</p>
            </div>
          </div>
          
          {/* Key Insight */}
          <div className="bg-gray-50 rounded-lg p-4 md:col-span-2">
            <h3 className="font-semibold text-gray-800 mb-2">Key Insight</h3>
            <p className="text-gray-700">
              Your PMO shows {normalizePercentage(strengths[0]?.percentage) >= 70 ? 'strong' : 'developing'} capabilities in {getCategoryDisplayName(strengths[0]?.categoryName as Category)} {' '}
              but could benefit from enhanced {getCategoryDisplayName(weaknesses[0]?.categoryName as Category)}.
            </p>

          </div>
        </div>
      </div>
      
      {/* Visual Results Section - 2x2 Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 border-b border-gray-100">
        {/* Category Performance Card - Flippable */}
        <div 
          className={`bg-gray-50 rounded-lg shadow-sm overflow-hidden p-4 md:col-span-2 lg:col-span-1 card-container ${isRadarCardFlipped ? 'card-flipped' : ''} clickable-card`}
          style={{ minHeight: '450px' }}
          onClick={flipRadarCard}
        >
          <div className="card-inner h-full">
            {/* Front of card - Radar Chart */}
            <div className="card-front">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-800">Category Performance</h3>
                <div className="text-xs text-gray-500 flex items-center">
                  <span>Click for details</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="h-80 flex justify-center items-center">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart outerRadius={110} data={radarData} cx="50%" cy="50%">
                    <PolarGrid />
                    <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12 }} />
                    <PolarRadiusAxis domain={[0, 100]} />
                    <Radar
                      name="Your PMO"
                      dataKey="A"
                      stroke="#1E40AF"
                      fill="#1E40AF"
                      fillOpacity={0.8}
                    />

                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Back of card - Detailed Scores */}
            <div className="card-back bg-gray-50 p-4" style={{ minHeight: '400px' }}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-800">Detailed Scores</h3>
                <div className="text-xs text-gray-500 flex items-center">
                  <span>Back to chart</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </div>
              </div>
              <div className="pr-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2">Category</th>
                      <th className="text-center py-2">Score</th>
                      <th className="text-right py-2">Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryResults.map((score, index) => { // Use categoryResults
                      // Maturity levels based on tool concept document
                      // Build Stage (20-39), Elevate Stage (40-59), Optimize Stage (60-80)
                      const level = normalizePercentage(score.percentage) >= 75 ? 'Optimize' : normalizePercentage(score.percentage) >= 60 ? 'Elevate' : 'Build';
                      const levelColor = normalizePercentage(score.percentage) >= 75 ? 'text-green-600' : normalizePercentage(score.percentage) >= 60 ? 'text-amber-600' : 'text-blue-600';
                      
                      return (
                        <tr key={index} className="border-b border-gray-100">
                          <td className="py-2 text-gray-700">{getCategoryDisplayName(score.categoryName as Category)}</td>
                          <td className="py-2 text-center">
                            <span className="font-medium">{score.score}/{score.maxPossible}</span>
                            <span className="text-gray-500 ml-1">({normalizePercentage(score.percentage)}%)</span>
                          </td>
                          <td className={`py-2 text-right font-medium ${levelColor}`}>{level}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        
        {/* Strengths & Weaknesses */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-800 mb-4">Strengths & Focus Areas</h3>
          
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-green-700 mb-2">Top Strengths</h4>
              <ul className="space-y-2">
                {strengths.slice(0, 2).map((score, index) => (
                  <li key={index} className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="text-xs font-normal">{getCategoryDisplayName(score.categoryName as Category)}</span>
                    <span className="ml-auto text-sm font-normal">{normalizePercentage(score.percentage)}%</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-amber-700 mb-2">Priority Focus Areas</h4>
              <ul className="space-y-2">
                {weaknesses.slice(0, 2).map((score, index) => (
                  <li key={index} className="flex items-center">
                    <span className="text-amber-500 mr-2">!</span>
                    <span className="text-xs font-normal">{getCategoryDisplayName(score.categoryName as Category)}</span>
                    <span className="ml-auto text-sm font-normal">{normalizePercentage(score.percentage)}%</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Maturity Journey</h4>
            <div className="relative pt-1">
              <div className="flex mb-2 items-center justify-between">
                <div className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-blue-600 bg-blue-100">
                  BUILD
                </div>
                <div className={`text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full ${maturityLevel === 'elevate' ? 'text-white bg-blue-600' : 'text-blue-600 bg-blue-100'}`}>
                  ELEVATE
                </div>
                <div className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-blue-600 bg-blue-100">
                  OPTIMIZE
                </div>
              </div>
              <div className="overflow-hidden h-2 mb-1 text-xs flex rounded bg-blue-200">
                <div style={{ width: `${scorePercentage}%` }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-600"></div>
              </div>
              <div className="flex justify-end">
                <span className="text-xs text-gray-500">Next: {currentLevel.nextStage}</span>
              </div>
            </div>
            
            <div className="flex mt-8 pt-2">
              <span className="text-xs text-green-600 font-medium flex items-center">
                <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
                Organizations at the {maturityLevel === 'build' ? 'Elevate' : 'Optimize'} stage typically see 15% higher project success rates.
              </span>
            </div>
          </div>
        </div>
        
        {/* PMO Development Path Header - Full Width */}
        <div className="bg-gray-50 rounded-lg p-4 md:col-span-2">
          <h3 className="font-semibold text-gray-800 mb-4">Your PMO Development Path</h3>
          <p className="text-sm text-gray-600 mb-4">Actionable recommendations based on your assessment results</p>
        </div>
        
        {/* Recommendation Cards - 3 column grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:col-span-2">
          {/* Quick Win Card */}
          <div className="bg-gray-50 rounded-lg shadow-sm overflow-hidden border border-gray-200 flex flex-col h-full">
            <div className="p-4 bg-blue-50 border-b border-gray-200">
              <div className="flex items-center">
                <div className="p-2 rounded-full bg-blue-100 mr-3">
                  <Award className="h-5 w-5 text-blue-600" />
                </div>
                <h4 className="font-medium text-gray-800">Recommendation 1</h4>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                <span className="font-medium">{recommendations.quickWin?.category}</span>
              </p>
            </div>
            <div className="p-4 flex-grow">
              {recommendations.quickWin ? (
                <div className="flex flex-col h-full">
                  <div>
                    <h5 className="font-medium text-gray-700 mb-2">{recommendations.quickWin.title}</h5>
                    <p className="text-gray-700">{recommendations.quickWin.recommendations[0]}</p>
                  </div>

                </div>
              ) : (
                <p className="text-gray-500 italic">No recommendation available</p>
              )}
            </div>
          </div>
          
          {/* Medium-Term Goal Card */}
          <div className="bg-gray-50 rounded-lg shadow-sm overflow-hidden border border-gray-200 flex flex-col h-full">
            <div className="p-4 bg-amber-50 border-b border-gray-200">
              <div className="flex items-center">
                <div className="p-2 rounded-full bg-amber-100 mr-3">
                  <TrendingUp className="h-5 w-5 text-amber-600" />
                </div>
                <h4 className="font-medium text-gray-800">Recommendation 2</h4>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                <span className="font-medium">{recommendations.mediumTerm?.category}</span>
              </p>
            </div>
            <div className="p-4 flex-grow">
              {recommendations.mediumTerm ? (
                <div className="flex flex-col h-full">
                  <div>
                    <h5 className="font-medium text-gray-700 mb-2">{recommendations.mediumTerm.title}</h5>
                    <p className="text-gray-700">{recommendations.mediumTerm.recommendations[0]}</p>
                  </div>

                </div>
              ) : (
                <p className="text-gray-500 italic">No recommendation available</p>
              )}
            </div>
          </div>
          
          {/* Long-Term Goal Card */}
          <div className="bg-gray-50 rounded-lg shadow-sm overflow-hidden border border-gray-200 flex flex-col h-full">
            <div className="p-4 bg-green-50 border-b border-gray-200">
              <div className="flex items-center">
                <div className="p-2 rounded-full bg-green-100 mr-3">
                  <ChevronRight className="h-5 w-5 text-green-600" />
                </div>
                <h4 className="font-medium text-gray-800">Recommendation 3</h4>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                <span className="font-medium">{recommendations.longTerm?.category}</span>
              </p>
            </div>
            <div className="p-4 flex-grow">
              {recommendations.longTerm ? (
                <div className="flex flex-col h-full">
                  <div>
                    <h5 className="font-medium text-gray-700 mb-2">{recommendations.longTerm.title}</h5>
                    <p className="text-gray-700">{recommendations.longTerm.recommendations[0]}</p>
                  </div>

                </div>
              ) : (
                <p className="text-gray-500 italic">No recommendation available</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="text-center md:col-span-2">
          <Button 
            className="radar-glow bg-deepBlue hover:bg-deepBlue-light text-white mx-auto"
            asChild
          >
            <a href="https://cal.com/noah.mccloud/optigolddiscoverycall">
              <Calendar className="mr-2 h-4 w-4" /> Schedule a Discovery Call
              <ChevronRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
          <p className="text-sm text-gray-500 mt-2">
            Get a customized roadmap for your PMO
          </p>
          <p className="text-xs text-gray-400 mt-6">
            Disclaimer: This tool provides general insights and is not a substitute for a professional PMO assessment. Results are for informational use only.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EnhancedResultsDashboard;
